import React, { useState, useEffect, Suspense, lazy, useCallback, useMemo } from 'react';
import LoadingScreen from './components/LoadingScreen';
import Header from './components/Header';
import { trackWebVitals, preloadCriticalResources, throttle } from './utils/performance';
import Hero from './components/Hero';
import BackToTop from './components/BackToTop';
import Toast from './components/Toast';

// Lazy load components for better performance
const Menu = lazy(() => import('./components/Menu'));
const About = lazy(() => import('./components/About'));
const Location = lazy(() => import('./components/Location'));
const Contact = lazy(() => import('./components/Contact'));
const Gallery = lazy(() => import('./components/Gallery'));
const Footer = lazy(() => import('./components/Footer'));
const Impressum = lazy(() => import('./components/Impressum'));
const Datenschutz = lazy(() => import('./components/Datenschutz'));

// Component loading fallback
const ComponentLoader = () => (
  <div className="flex items-center justify-center py-16">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [currentPage, setCurrentPage] = useState('home');

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    // Handle URL hash changes for navigation
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      if (hash === 'impressum') {
        setCurrentPage('impressum');
      } else if (hash === 'datenschutz') {
        setCurrentPage('datenschutz');
      } else {
        setCurrentPage('home');
      }
    };

    // Check initial hash
    handleHashChange();
    
    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Optimized scroll handler with throttling
  const handleScroll = useCallback(() => {
    setShowBackToTop(window.pageYOffset > 300);
  }, []);

  const throttledHandleScroll = useMemo(
    () => throttle(handleScroll, 100),
    [handleScroll]
  );

  useEffect(() => {
    // Preload critical resources
    preloadCriticalResources();
    
    // Track web vitals for performance monitoring
    trackWebVitals((metric) => {
      console.log('Web Vital:', metric);
    });

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, [throttledHandleScroll]);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (currentPage === 'impressum') {
    return (
      <div className="App">
        <Header />
        <Suspense fallback={<ComponentLoader />}>
          <Impressum />
          <Footer />
        </Suspense>
        {showBackToTop && <BackToTop />}
        <Toast toast={toast} />
      </div>
    );
  }

  if (currentPage === 'datenschutz') {
    return (
      <div className="App">
        <Header />
        <Suspense fallback={<ComponentLoader />}>
          <Datenschutz />
          <Footer />
        </Suspense>
        {showBackToTop && <BackToTop />}
        <Toast toast={toast} />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <Hero />
      <Suspense fallback={<ComponentLoader />}>
        <Menu />
      </Suspense>
      <Suspense fallback={<ComponentLoader />}>
        <About />
      </Suspense>
      <Suspense fallback={<ComponentLoader />}>
        <Location />
      </Suspense>
      <Suspense fallback={<ComponentLoader />}>
        <Contact showToast={showToast} />
      </Suspense>
      <Suspense fallback={<ComponentLoader />}>
        <Gallery />
      </Suspense>
      <Suspense fallback={<ComponentLoader />}>
        <Footer />
      </Suspense>
      {showBackToTop && <BackToTop />}
      <Toast toast={toast} />
    </div>
  );
}

export default App;